package com.merkost.handpass.admin.container

import com.merkost.handpass.admin.api.AdminRepositoryApi
import com.merkost.handpass.admin.contract.AsyncSection
import com.merkost.handpass.admin.contract.QueueAction
import com.merkost.handpass.admin.contract.QueueBulkActionKind
import com.merkost.handpass.admin.contract.QueueIntent
import com.merkost.handpass.admin.contract.QueueSortFilter
import com.merkost.handpass.admin.contract.QueueSourceFilter
import com.merkost.handpass.admin.contract.QueueState
import com.merkost.handpass.admin.contract.QueueViewFilter
import com.merkost.handpass.admin.platform.WindowFocusTrigger
import com.merkost.handpass.admin.util.toAdminMessage
import com.merkost.handpass.api.dto.admin.AdminQueueItem
import com.merkost.handpass.api.dto.admin.BulkActionItem
import com.merkost.handpass.api.dto.admin.BulkActionItemType
import com.merkost.handpass.api.dto.admin.BulkActionKind
import com.merkost.handpass.api.dto.admin.BulkActionRequest
import com.merkost.handpass.api.dto.admin.SavedView
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import pro.respawn.flowmvi.api.Container
import pro.respawn.flowmvi.api.PipelineContext
import pro.respawn.flowmvi.dsl.lazyStore
import pro.respawn.flowmvi.plugins.recover
import pro.respawn.flowmvi.plugins.reduce

class QueueContainer(
    private val repo: AdminRepositoryApi,
    private val focusTrigger: WindowFocusTrigger,
) : Container<QueueState, QueueIntent, QueueAction> {

    private val scope = MainScope()

    override val store by lazyStore(QueueState(), scope) {
        configure { name = "QueueStore" }
        recover { e ->
            updateState { copy(data = AsyncSection.Failed(e.toAdminMessage())) }
            null
        }
        reduce { intent ->
            when (intent) {
                is QueueIntent.SetSource -> { updateState { copy(source = intent.source, page = 1) }; reload() }
                is QueueIntent.SetSort -> { updateState { copy(sort = intent.sort, page = 1) }; reload() }
                is QueueIntent.SetReason -> { updateState { copy(reason = intent.reason, page = 1) }; reload() }
                is QueueIntent.SetView -> { updateState { copy(view = intent.view, page = 1) }; reload() }
                is QueueIntent.GoToPage -> { updateState { copy(page = intent.page) }; reload() }
                is QueueIntent.ToggleSelect -> updateState {
                    copy(selectedIds = if (intent.id in selectedIds) selectedIds - intent.id else selectedIds + intent.id)
                }
                QueueIntent.ToggleSelectAllVisible -> {
                    val s = currentState()
                    val visibleIds = (s.data as? AsyncSection.Loaded)?.value?.map { it.id }?.toSet() ?: emptySet()
                    updateState {
                        copy(selectedIds = if (visibleIds.all { it in selectedIds }) selectedIds - visibleIds else selectedIds + visibleIds)
                    }
                }
                QueueIntent.ClearSelection -> updateState { copy(selectedIds = emptySet()) }
                is QueueIntent.TapItem -> {
                    val path = when (val item = intent.item) {
                        is AdminQueueItem.ListingReportItem -> "/listings/${item.listingId}"
                        is AdminQueueItem.UserReportItem -> "/users/${item.reportedUserId}"
                        is AdminQueueItem.ActiveListingItem -> "/listings/${item.id}"
                    }
                    action(QueueAction.Navigate(path))
                }
                is QueueIntent.TapBulkAction -> {
                    val s = currentState()
                    action(QueueAction.OpenBulkConfirm(intent.kind, s.selectedIds.size))
                }
                is QueueIntent.ConfirmBulkAction -> runBulkAction(intent.kind, intent.reason)
                is QueueIntent.SaveCurrentView -> saveCurrentView(intent.name)
                is QueueIntent.ApplySavedView -> applySavedView(intent.view)
                is QueueIntent.DeleteSavedView -> deleteSavedView(intent.viewId)
                is QueueIntent.LoadSavedViews -> updateState { copy(savedViews = intent.views) }
                is QueueIntent.ReactToFocusTick, QueueIntent.Refresh -> reload()
            }
        }
    }

    init {
        scope.launch {
            focusTrigger.ticks.collect { tick -> store.intent(QueueIntent.ReactToFocusTick(tick)) }
        }
        scope.launch {
            val savedViews = repo.getSavedViews()?.views ?: emptyList()
            store.intent(QueueIntent.LoadSavedViews(savedViews))
        }
    }

    private suspend fun PipelineContext<QueueState, QueueIntent, QueueAction>.currentState(): QueueState {
        var s = QueueState()
        withState { s = this }
        return s
    }

    private suspend fun PipelineContext<QueueState, QueueIntent, QueueAction>.reload() {
        val s = currentState()
        updateState { copy(data = AsyncSection.Loading) }
        val response = repo.getQueue(
            page = s.page,
            pageSize = 20,
            source = s.source.wire,
            sort = s.sort.wire,
            reason = s.reason,
            view = s.view?.wire,
        )
        if (response == null) {
            updateState { copy(data = AsyncSection.Failed("Failed to fetch queue")) }
            return
        }
        updateState {
            copy(
                data = AsyncSection.Loaded(response.data),
                totalPages = response.meta?.totalPages ?: 1,
                totalCount = response.meta?.totalCount ?: 0L,
            )
        }
    }

    private suspend fun PipelineContext<QueueState, QueueIntent, QueueAction>.runBulkAction(kind: QueueBulkActionKind, reason: String?) {
        val s = currentState()
        val visibleItems = (s.data as? AsyncSection.Loaded)?.value ?: emptyList()
        val selectedItems = visibleItems.filter { it.id in s.selectedIds }
        val request = BulkActionRequest(
            items = selectedItems.map { item ->
                val type = when (item) {
                    is AdminQueueItem.ListingReportItem -> BulkActionItemType.LISTING_REPORT
                    is AdminQueueItem.UserReportItem -> BulkActionItemType.USER_REPORT
                    is AdminQueueItem.ActiveListingItem -> BulkActionItemType.LISTING
                }
                BulkActionItem(type = type, id = item.id)
            },
            action = when (kind) {
                QueueBulkActionKind.Dismiss -> BulkActionKind.DISMISS
                QueueBulkActionKind.SuspendListing -> BulkActionKind.SUSPEND_LISTING
                QueueBulkActionKind.WarnUser -> BulkActionKind.WARN_USER
                QueueBulkActionKind.SuspendUser -> BulkActionKind.SUSPEND_USER
                QueueBulkActionKind.BanUser -> BulkActionKind.BAN_USER
            },
            reason = reason,
            durationDays = if (kind == QueueBulkActionKind.SuspendUser) 7 else null,
        )
        repo.bulkAction(request).fold(
            onSuccess = { resp ->
                val successes = resp.results.count { it.success }
                val failures = resp.results.size - successes
                if (failures == 0) {
                    action(QueueAction.ShowSuccess("$successes item(s) processed"))
                } else {
                    action(QueueAction.ShowPartialSuccess(successes, failures))
                }
                updateState { copy(selectedIds = emptySet()) }
                reload()
            },
            onFailure = { e -> action(QueueAction.ShowError(e.toAdminMessage())) },
        )
    }

    private suspend fun PipelineContext<QueueState, QueueIntent, QueueAction>.saveCurrentView(name: String) {
        val s = currentState()
        val newView = SavedView(
            id = "view_${kotlinx.browser.window.performance.now().toLong()}",
            name = name,
            filterQuery = serializeFilters(s),
            createdAt = "",
        )
        val newList = (s.savedViews + newView).take(10)
        repo.putSavedViews(newList).onSuccess {
            updateState { copy(savedViews = newList) }
            action(QueueAction.ShowSuccess("Saved view: $name"))
        }.onFailure { e -> action(QueueAction.ShowError(e.toAdminMessage())) }
    }

    private suspend fun PipelineContext<QueueState, QueueIntent, QueueAction>.applySavedView(view: SavedView) {
        val parsed = parseFilters(view.filterQuery)
        updateState { copy(source = parsed.source, sort = parsed.sort, reason = parsed.reason, view = parsed.view, page = 1) }
        reload()
    }

    private suspend fun PipelineContext<QueueState, QueueIntent, QueueAction>.deleteSavedView(viewId: String) {
        val s = currentState()
        val newList = s.savedViews.filterNot { it.id == viewId }
        repo.putSavedViews(newList).onSuccess {
            updateState { copy(savedViews = newList) }
        }
    }

    private fun serializeFilters(state: QueueState): String = buildString {
        append("?source=${state.source.wire}")
        append("&sort=${state.sort.wire}")
        if (state.reason != null) append("&reason=${state.reason}")
        if (state.view != null) append("&view=${state.view.wire}")
    }

    private fun parseFilters(query: String): FilterTuple {
        val params = query.removePrefix("?").split("&").mapNotNull {
            val parts = it.split("=", limit = 2)
            if (parts.size == 2) parts[0] to parts[1] else null
        }.toMap()
        return FilterTuple(
            source = params["source"]?.let { runCatching { QueueSourceFilter.valueOf(it) }.getOrNull() } ?: QueueSourceFilter.ALL,
            sort = params["sort"]?.let { runCatching { QueueSortFilter.valueOf(it) }.getOrNull() } ?: QueueSortFilter.NEWEST,
            reason = params["reason"],
            view = params["view"]?.let { runCatching { QueueViewFilter.valueOf(it) }.getOrNull() },
        )
    }

    private data class FilterTuple(
        val source: QueueSourceFilter,
        val sort: QueueSortFilter,
        val reason: String?,
        val view: QueueViewFilter?,
    )
}
